import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { CopyIcon, DownloadIcon, ExternalLinkIcon } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { visitorRegistrationPublicUrl } from "@/shared/lib/config";

async function downloadDataUrl(dataUrl: string, filename: string) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

export function VisitorRegistrationQrCard() {
  const registerUrl = visitorRegistrationPublicUrl();
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void QRCode.toDataURL(registerUrl, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: "M",
    }).then((url) => {
      if (!cancelled) setPreviewSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [registerUrl]);

  const handleDownload = async () => {
    setBusy(true);
    try {
      const dataUrl = await QRCode.toDataURL(registerUrl, {
        width: 1024,
        margin: 2,
        errorCorrectionLevel: "M",
      });
      await downloadDataUrl(
        dataUrl,
        "fanglao-visitor-register-qr.png",
      );
      toast.success("ດາວໂຫຼດ QR ແລ້ວ");
    } catch {
      toast.error("ດາວໂຫຼດ QR ບໍ່ສຳເລັດ");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(registerUrl);
      toast.success("ຄັດລອກລິ້ງແລ້ວ");
    } catch {
      toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>QR ລົງທະບຽນຜູ້ເຂົ້າຊົມ</CardTitle>
        <CardDescription>
          ສະແກນເພື່ອເປີດໜ້າລົງທະບຽນ &amp; ລຸ້ນລາງວັນ — ດາວໂຫຼດໄປພິມໄດ້
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="mx-auto shrink-0 rounded-lg border bg-white p-3 sm:mx-0">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="QR ลงทะเบียนผู้เข้าชม"
              className="size-44"
            />
          ) : (
            <div className="bg-muted size-44 animate-pulse rounded" />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="space-y-1">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              Public link
            </p>
            <a
              href={registerUrl}
              target="_blank"
              rel="noreferrer"
              className="text-primary break-all text-sm font-medium underline-offset-4 hover:underline"
            >
              {registerUrl}
            </a>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => void handleDownload()}
              disabled={busy}
            >
              <DownloadIcon className="mr-2 size-4" />
              {busy ? "ກຳລັງດາວໂຫຼດ..." : "ດາວໂຫຼດ QR (PNG)"}
            </Button>
            <Button type="button" variant="outline" onClick={() => void handleCopy()}>
              <CopyIcon className="mr-2 size-4" />
              ຄັດລອກລິ້ງ
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => window.open(registerUrl, "_blank", "noopener,noreferrer")}
            >
              <ExternalLinkIcon className="mr-2 size-4" />
              ເປີດໜ້າ
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
